import { useState } from "react";

// Premium team-member card. Renders a real portrait when `photo` is provided
// AND loads successfully; otherwise falls back to a monogram avatar variant
// that matches the editorial aesthetic (no watermarked stock imagery).

const GRADIENTS = [
  "from-clay-500/25 via-clay-600/10 to-ink-800",
  "from-ink-700 via-ink-800 to-clay-500/15",
  "from-clay-400/20 via-ink-700 to-ink-900",
  "from-ink-800 via-clay-500/15 to-ink-700",
  "from-clay-600/20 via-ink-800 to-ink-900",
];

function pickGradient(slug) {
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  return GRADIENTS[h % GRADIENTS.length];
}

// Prefer real photos only for URLs we control (local /team/*), so we never
// re-introduce watermarked imagery from third-party origins.
function isTrustedPhoto(url) {
  if (!url) return false;
  return url.startsWith("/team/") || url.startsWith("data:");
}

export default function TeamMember({ member, testid }) {
  const [imgOk, setImgOk] = useState(true);
  const initials = member.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2);
  const gradient = pickGradient(member.slug);
  const showPhoto = isTrustedPhoto(member.photo) && imgOk;

  return (
    <div className="card-surface p-6 h-full" data-testid={testid}>
      <div
        className={`aspect-[4/5] rounded-md mb-6 relative overflow-hidden bg-gradient-to-br ${gradient} border border-white/10`}
      >
        {showPhoto ? (
          <>
            <img
              src={member.photo}
              alt={member.name}
              onError={() => setImgOk(false)}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-ink-950/70 to-transparent pointer-events-none" />
            <div className="absolute top-4 left-4 text-[10px] uppercase tracking-widest2 text-bone-100/90 drop-shadow">
              {member.department}
            </div>
          </>
        ) : (
          <>
            <div className="absolute top-4 left-4 text-[10px] uppercase tracking-widest2 text-bone-200/60">
              {member.department}
            </div>
            <div
              aria-hidden
              className="absolute -right-4 -bottom-6 font-display text-[10rem] leading-none text-bone-100/[0.05] select-none pointer-events-none"
            >
              {initials}
            </div>
            <div className="absolute inset-0 flex items-end p-6">
              <span className="font-display text-6xl text-clay-400 leading-none">
                {initials}
              </span>
            </div>
            <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-white/[0.05] to-transparent pointer-events-none" />
          </>
        )}
      </div>

      <h3 className="font-display text-xl text-bone-100 mb-1 leading-tight">
        {member.name}
      </h3>
      <p className="text-xs uppercase tracking-widest2 text-clay-400 mb-4">
        {member.role}
      </p>
      <p className="text-sm text-bone-200/75 leading-relaxed">{member.short}</p>
    </div>
  );
}
